import { createContext, useContext, useEffect, useRef } from 'react'

const CodeRefContext = createContext<React.MutableRefObject<string> | null>(
  null,
)

export function CodeRefProvider({
  problemSlug,
  children,
}: {
  problemSlug?: string | null
  children: React.ReactNode
}) {
  const codeRef = useRef('')

  // The ref belongs to the problem the code was written for. Clear it when
  // the slug changes so an early AI message on the new problem sends an empty
  // string instead of the previous problem's code — the code pane re-syncs
  // the ref once the new draft loads into Monaco. Child effects run first,
  // so this clear runs after any stale sync from the code pane.
  const prevSlugRef = useRef(problemSlug)
  useEffect(() => {
    if (prevSlugRef.current === problemSlug) return
    prevSlugRef.current = problemSlug
    codeRef.current = ''
  }, [problemSlug])

  return (
    <CodeRefContext.Provider value={codeRef}>
      {children}
    </CodeRefContext.Provider>
  )
}

export function useCodeRef() {
  const ref = useContext(CodeRefContext)
  if (!ref) throw new Error('useCodeRef must be used within CodeRefProvider')
  return ref
}

export function useCodeRefOptional() {
  return useContext(CodeRefContext)
}
